// 149-cw-r113 (2026-08-29) — Helper untuk hitung nominal pembayaran diterima.
// Skema baru: `entry.payment_receipts[]` array of {id, date, amount, bank_name, account_no, note, created_by_name}.
// Legacy: entry lama yang punya `entry.date` diisi tapi TIDAK punya receipts → dianggap "Lunas legacy" (total_received = expected_net).

/**
 * Hitung ekspektasi Net Received (jumlah yang seharusnya diterima setelah PPh + retensi).
 * 2026-06-20 (user request rekonsiliasi): PARITY penuh dgn backend
 * `_expected_net_amount` (utils/computation.py) — snapshot billing engine →
 * net tersimpan (net_received_amount/net_amount/net_received) → formula
 * gross − PPh − retensi inside (retensi outside TIDAK dipotong di sini).
 * Satu fungsi penerimaan untuk Ringkasan, Pembayaran Masuk, Monitoring, Kas & Bank.
 */
export function computeExpectedNet(entry) {
  if (!entry) return 0;
  const net = Number(entry.net_received_amount) || Number(entry.net_amount) || Number(entry.net_received) || 0;
  if (net > 0) return net;
  const a = Number(entry.gross_invoice_amount ?? entry.invoice_amount ?? entry.amount) || 0;
  if (a <= 0) return 0;
  const p = Number(entry.pph_amount) || 0;
  // 2026-06-21 (user report): retensi ikut diturunkan dari persen bila
  // retention_amount kosong — fallback berantai entry → `_src_*` → project.
  const rType = (entry.retention_type || entry._src_retention_type || entry.__project?.retention_type || "inside");
  let r = rType === "inside" ? Number(entry.retention_amount) || 0 : 0;
  if (r <= 0 && rType === "inside") {
    const rp = Number(entry.retention_pct ?? entry._src_retention_pct ?? entry.__project?.retention_pct) || 0;
    const base = Number(entry.amount ?? entry.gross_invoice_amount ?? entry.invoice_amount) || 0;
    if (rp > 0 && rp < 100 && base > 0) r = Math.round(((base * rp) / 100) * 100) / 100;
  }
  const exp = a - p - r;
  return exp > 0 ? exp : a;
}

/**
 * Total nominal diterima dari semua payment_receipts.
 * Kalau receipts kosong tapi entry punya legacy `date` → return expected_net (dianggap lunas).
 */
export function computeTotalReceived(entry) {
  if (!entry) return 0;
  const receipts = entry.payment_receipts || [];
  if (receipts.length > 0) {
    return receipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }
  // Legacy: entry.date diisi tanpa receipts → dianggap sudah lunas penuh
  if (entry.date) {
    return computeExpectedNet(entry);
  }
  return 0;
}

/**
 * Status pembayaran invoice.
 * @returns {"waiting" | "partial" | "paid"}
 */
export function getReceiptStatus(entry) {
  const expected = computeExpectedNet(entry);
  const received = computeTotalReceived(entry);
  if (received <= 0) return "waiting";
  // 2026-02-13 (user report): toleransi Rp1 (parity backend) — selisih fraksi
  // rupiah dari persen PPh tidak boleh membuat status "partial".
  if (expected > 0 && received + 1 < expected) return "partial";
  return "paid";
}
